import React from "react";
import {
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Clock3,
  Building2,
  ArrowRight,
  Sparkles,
  FileText,
} from "lucide-react";

const ContactUs = () => {
  const features = [
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: "Trusted Property Process",
      desc: "Professional and verified assistance for all property services.",
      color: "text-red-500 bg-red-50",
    },
    {
      icon: <Clock3 className="w-6 h-6" />,
      title: "Fast Response",
      desc: "Quick consultation and support from our expert team.",
      color: "text-green-500 bg-green-50",
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Bangalore Experts",
      desc: "Specialized assistance for Bangalore property registrations.",
      color: "text-blue-500 bg-blue-50",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FAFC] via-white to-[#EEF2FF] py-24 px-4 md:px-10 -mt-20">
      
      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-red-100 rounded-full blur-3xl opacity-40"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-40"></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          
          <div className="inline-flex items-center gap-2 bg-white border border-red-100 shadow-md px-5 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">
              Contact HousyHunt
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black leading-tight text-gray-900">
            Let’s Build Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#E63946] via-[#ff4d5a] to-[#ff7b7b]">
              Property Journey
            </span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Connect with HousyHunt for premium property registration,
            documentation, and legal assistance services in Bangalore.
            Our experts ensure a smooth and hassle-free experience.
          </p>
        </div>

        {/* MAIN CONTENT */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* LEFT IMAGE */}
          <div className="relative">
            
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.12)] border border-white/50">
              
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop"
                alt="Corporate Building"
                className="w-full h-[700px] object-cover"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

              {/* Floating Label */}
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-xl border border-white shadow-xl px-5 py-3 rounded-2xl">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-red-500">
                  Bangalore Property Experts
                </p>

                <h4 className="text-sm font-semibold text-gray-800 mt-1">
                  Trusted by Property Owners
                </h4>
              </div>

              {/* Bottom Content */}
              <div className="absolute bottom-8 left-8 right-8">
                <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
                  Corporate-Level
                  <span className="block text-red-400">
                    Property Support
                  </span>
                </h2>

                <p className="text-gray-200 mt-5 text-lg max-w-lg leading-relaxed">
                  Premium assistance for e-Khata registration, legal
                  verification, and documentation services across Bangalore.
                </p>
              </div>
            </div>

            {/* Floating Glass Card */}
            <div className="hidden md:block absolute -bottom-8 -left-8 bg-white/80 backdrop-blur-2xl border border-white rounded-3xl p-6 shadow-2xl w-72">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 text-green-600 p-4 rounded-2xl">
                  <ShieldCheck className="w-6 h-6" />
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 text-lg">
                    100% Trusted Assistance
                  </h4>

                  <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                    Dedicated property support with secure documentation guidance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT CONTACT SECTION */}
          <div className="flex flex-col justify-center">
            
            <div className="bg-white/80 backdrop-blur-2xl border border-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_20px_80px_rgba(0,0,0,0.08)]">
              
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-red-500">
                Get In Touch
              </p>

              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-4 leading-tight">
                We’re Here To Help
              </h2>

              <p className="text-gray-500 mt-5 text-lg leading-relaxed">
                Reach out to our team for any property-related assistance.
                We’ll guide you through every step professionally and efficiently.
              </p>

              {/* CONTACT CARDS */}
              <div className="mt-10 space-y-6">

                {/* WhatsApp Enquiry */}
                <a
                  href="https://wa.me/message/MDG2ZSZNG6P6G1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between bg-gradient-to-r from-red-600 to-red-700 text-white border border-red-500 rounded-3xl p-6 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                >
                  <div className="flex items-center gap-5">
                    <div className="bg-white/20 text-white p-4 rounded-2xl">
                      <FileText className="w-7 h-7" />
                    </div>

                    <div>
                      <p className="text-sm text-red-100 font-medium">
                        Instant Assistance
                      </p>

                      <h3 className="text-2xl font-black text-white">
                        Chat on WhatsApp
                      </h3>
                    </div>
                  </div>

                  <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-all" />
                </a>
                
                {/* Phone */}
                <a
                  href="tel:8088687429"
                  className="group flex items-center justify-between bg-gradient-to-r from-white to-gray-50 border border-gray-100 hover:border-red-200 rounded-3xl p-6 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex items-center gap-5">
                    <div className="bg-red-50 text-red-500 p-4 rounded-2xl">
                      <Phone className="w-7 h-7" />
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        Phone Number
                      </p>

                      <h3 className="text-2xl font-black text-gray-900">
                        +91 80886 87429
                      </h3>
                    </div>
                  </div>

                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-red-500 group-hover:translate-x-1 transition-all" />
                </a>

                {/* Email */}
                <a
                  href="mailto:hello@housyhunt.com"
                  className="group flex items-center justify-between bg-gradient-to-r from-white to-gray-50 border border-gray-100 hover:border-green-200 rounded-3xl p-6 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex items-center gap-5">
                    <div className="bg-green-50 text-green-500 p-4 rounded-2xl">
                      <Mail className="w-7 h-7" />
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        Email Address
                      </p>

                      <h3 className="text-2xl font-black text-gray-900">
                        hello@housyhunt.com
                      </h3>
                    </div>
                  </div>

                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all" />
                </a>

                {/* Location */}
                <div className="flex items-center justify-between bg-gradient-to-r from-white to-gray-50 border border-gray-100 rounded-3xl p-6">
                  <div className="flex items-center gap-5">
                    <div className="bg-blue-50 text-blue-500 p-4 rounded-2xl">
                      <MapPin className="w-7 h-7" />
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        Service Area
                      </p>

                      <h3 className="text-2xl font-black text-gray-900">
                        Bangalore, Karnataka
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FEATURES */}
            <div className="grid md:grid-cols-3 gap-5 mt-8">
              {features.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-xl border border-white rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className={`w-fit p-4 rounded-2xl ${item.color}`}>
                    {item.icon}
                  </div>

                  <h3 className="mt-5 text-xl font-black text-gray-900">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-gray-500 leading-relaxed text-sm">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;